import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  Star, Plus, Loader2, ThumbsUp, MessageSquare, 
  TrendingUp, Edit, Trash2, Sparkles, Award, Heart
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import { toast } from 'sonner';
import { PageHeader } from '../components/common/PageHeader';

const categoryLabels = {
  organization: 'Organização',
  communication: 'Comunicação',
  activities: 'Atividades',
  accommodation: 'Hospedagem',
  value_for_money: 'Custo-benefício',
};

const highlightSuggestions = [
  'Paisagens incríveis',
  'Ótima companhia',
  'Comida deliciosa',
  'Atividades divertidas',
  'Preço justo',
  'Boa organização',
  'Hospedagem confortável',
  'Momentos inesquecíveis',
];

function StarRating({ value, onChange, size = 'md', readonly = false }) {
  const [hover, setHover] = useState(0);
  
  const sizes = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
  };

  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          className={`${readonly ? 'cursor-default' : 'cursor-pointer'} transition-transform hover:scale-110`}
          onMouseEnter={() => !readonly && setHover(star)}
          onMouseLeave={() => !readonly && setHover(0)}
          onClick={() => !readonly && onChange(star)}
        >
          <Star
            className={`${sizes[size]} ${
              star <= (hover || value)
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-slate-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
}

export default function TripReviews() {
  const { tripId } = useParams();
  const context = useOutletContext();
  const { trip } = context || {};
  const { api } = useAuth();
  
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState(null);
  const [myReview, setMyReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    overall_rating: 0,
    category_ratings: {
      organization: 0,
      communication: 0,
      activities: 0,
      accommodation: 0,
      value_for_money: 0,
    },
    comment: '',
    highlights: [],
    improvements: '',
    would_travel_again: true,
  });

  useEffect(() => {
    fetchData();
  }, [tripId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [reviewsRes, summaryRes, myReviewRes] = await Promise.all([
        api.get(`/trips/${tripId}/reviews`),
        api.get(`/trips/${tripId}/reviews/summary`),
        api.get(`/trips/${tripId}/reviews/my-review`)
      ]);
      setReviews(reviewsRes.data);
      setSummary(summaryRes.data);
      setMyReview(myReviewRes.data);
      
      // If editing existing review, populate form
      if (myReviewRes.data) {
        setFormData({
          overall_rating: myReviewRes.data.overall_rating,
          category_ratings: myReviewRes.data.category_ratings || {
            organization: 0, communication: 0, activities: 0,
            accommodation: 0, value_for_money: 0,
          },
          comment: myReviewRes.data.comment,
          highlights: myReviewRes.data.highlights || [],
          improvements: myReviewRes.data.improvements?.join(', ') || '',
          would_travel_again: myReviewRes.data.would_travel_again,
        });
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
      if (error.response?.status === 400) {
        // Trip hasn't ended yet
        toast.error(error.response.data.detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.overall_rating === 0) {
      toast.error('Selecione uma nota geral');
      return;
    }
    if (formData.comment.length < 10) {
      toast.error('O comentário deve ter pelo menos 10 caracteres');
      return;
    }

    setSubmitting(true);
    try {
      const data = {
        overall_rating: formData.overall_rating,
        category_ratings: Object.values(formData.category_ratings).some(v => v > 0) 
          ? formData.category_ratings 
          : null,
        comment: formData.comment,
        highlights: formData.highlights.length > 0 ? formData.highlights : null,
        improvements: formData.improvements 
          ? formData.improvements.split(',').map(s => s.trim()).filter(Boolean)
          : null,
        would_travel_again: formData.would_travel_again,
      };

      if (myReview) {
        await api.put(`/trips/${tripId}/reviews/${myReview.id}`, data);
        toast.success('Avaliação atualizada!');
      } else {
        await api.post(`/trips/${tripId}/reviews`, data);
        toast.success('Avaliação enviada! Obrigado pelo feedback! 🎉');
      }
      
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar avaliação');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!myReview || !window.confirm('Excluir sua avaliação?')) return;
    
    try {
      await api.delete(`/trips/${tripId}/reviews/${myReview.id}`);
      toast.success('Avaliação excluída');
      setMyReview(null);
      setFormData({
        overall_rating: 0,
        category_ratings: { organization: 0, communication: 0, activities: 0, accommodation: 0, value_for_money: 0 },
        comment: '',
        highlights: [],
        improvements: '',
        would_travel_again: true,
      });
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir avaliação');
    }
  };

  const toggleHighlight = (highlight) => {
    setFormData(prev => ({
      ...prev,
      highlights: prev.highlights.includes(highlight)
        ? prev.highlights.filter(h => h !== highlight)
        : [...prev.highlights, highlight]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Avaliações da Viagem"
        subtitle="Compartilhe sua experiência e ajude em viagens futuras"
        helpScreen="reviews"
        actions={
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="rounded-full bg-orange-500 hover:bg-orange-600"
                data-testid="write-review-btn"
              >
                {myReview ? (
                  <>
                    <Edit className="w-4 h-4 mr-2" />
                    Editar avaliação
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Avaliar viagem
                  </>
                )}
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-500" />
                {myReview ? 'Editar avaliação' : 'Avaliar viagem'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              {/* Overall Rating */}
              <div className="text-center">
                <Label className="block mb-3">Nota geral *</Label>
                <StarRating
                  value={formData.overall_rating}
                  onChange={(v) => setFormData({ ...formData, overall_rating: v })}
                  size="lg"
                />
                <p className="text-sm text-slate-500 mt-2">
                  {formData.overall_rating === 0 && 'Selecione uma nota'}
                  {formData.overall_rating === 1 && 'Muito ruim 😞'}
                  {formData.overall_rating === 2 && 'Ruim 😕'}
                  {formData.overall_rating === 3 && 'Regular 😐'}
                  {formData.overall_rating === 4 && 'Bom 😊'}
                  {formData.overall_rating === 5 && 'Excelente! 🤩'}
                </p>
              </div>

              {/* Category Ratings */}
              <div>
                <Label className="block mb-3">Avaliação por categoria (opcional)</Label>
                <div className="space-y-3">
                  {Object.entries(categoryLabels).map(([key, label]) => (
                    <div key={key} className="flex items-center justify-between">
                      <span className="text-sm text-slate-600 dark:text-slate-400">{label}</span>
                      <StarRating
                        value={formData.category_ratings[key]}
                        onChange={(v) => setFormData({
                          ...formData,
                          category_ratings: { ...formData.category_ratings, [key]: v }
                        })}
                        size="sm"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Comment */}
              <div>
                <Label>Seu comentário *</Label>
                <Textarea
                  value={formData.comment}
                  onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                  placeholder="Conte como foi sua experiência..."
                  className="mt-1"
                  rows={4}
                  data-testid="review-comment"
                />
                <p className="text-xs text-slate-400 mt-1">
                  Mínimo 10 caracteres ({formData.comment.length}/1000)
                </p>
              </div>

              {/* Highlights */}
              <div>
                <Label className="block mb-2">Destaques da viagem</Label>
                <div className="flex flex-wrap gap-2">
                  {highlightSuggestions.map((highlight) => (
                    <Badge
                      key={highlight}
                      variant={formData.highlights.includes(highlight) ? 'default' : 'outline'}
                      className={`cursor-pointer ${
                        formData.highlights.includes(highlight)
                          ? 'bg-orange-500 hover:bg-orange-600'
                          : 'hover:bg-slate-100'
                      }`}
                      onClick={() => toggleHighlight(highlight)}
                    >
                      {highlight}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Improvements */}
              <div>
                <Label>Sugestões de melhoria (opcional)</Label>
                <Input
                  value={formData.improvements}
                  onChange={(e) => setFormData({ ...formData, improvements: e.target.value })}
                  placeholder="Separe por vírgulas..."
                  className="mt-1"
                />
              </div>

              {/* Would travel again */}
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-all ${
                    formData.would_travel_again
                      ? 'bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-700 text-green-700 dark:text-green-400'
                      : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-600 text-slate-500 dark:text-slate-400'
                  }`}
                  onClick={() => setFormData({ ...formData, would_travel_again: true })}
                >
                  <ThumbsUp className="w-4 h-4" />
                  Viajaria novamente
                </button>
                <button
                  type="button"
                  className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-all ${
                    !formData.would_travel_again
                      ? 'bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-700 text-red-700 dark:text-red-400'
                      : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-600 text-slate-500 dark:text-slate-400'
                  }`}
                  onClick={() => setFormData({ ...formData, would_travel_again: false })}
                >
                  Não viajaria
                </button>
              </div>

              <div className="flex gap-3 pt-2">
                {myReview && (
                  <Button
                    type="button"
                    variant="outline"
                    className="text-red-500 hover:text-red-600"
                    onClick={handleDelete}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 rounded-full"
                  onClick={() => setDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 rounded-full bg-orange-500 hover:bg-orange-600"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : myReview ? (
                    'Salvar'
                  ) : (
                    'Enviar avaliação'
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        }
      />

      {/* Summary Card */}
      {summary && summary.total_reviews > 0 && (
        <Card className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 border-amber-200 dark:border-amber-800">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Average Rating */}
              <div className="text-center lg:border-r lg:border-amber-200 dark:border-amber-800">
                <div className="text-5xl font-bold text-amber-600">{summary.average_rating}</div>
                <StarRating value={Math.round(summary.average_rating)} readonly size="sm" />
                <p className="text-sm text-slate-600 mt-2">
                  {summary.total_reviews} avaliação{summary.total_reviews !== 1 ? 'ões' : ''}
                </p>
              </div>
              
              {/* Rating Distribution */}
              <div className="space-y-2">
                {[5, 4, 3, 2, 1].map((rating) => {
                  const count = summary.rating_distribution[rating] || 0;
                  const percentage = summary.total_reviews > 0 
                    ? (count / summary.total_reviews) * 100 
                    : 0;
                  return (
                    <div key={rating} className="flex items-center gap-2">
                      <span className="text-sm text-slate-600 w-3">{rating}</span>
                      <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                      <Progress value={percentage} className="h-2 flex-1" />
                      <span className="text-xs text-slate-500 w-8">{count}</span>
                    </div>
                  );
                })}
              </div>

              {/* Stats */}
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-white dark:bg-slate-800 rounded-lg">
                  <ThumbsUp className="w-5 h-5 text-green-500" />
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">
                      {summary.would_travel_again_percentage}%
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">viajariam novamente</p>
                  </div>
                </div>
                
                {summary.highlights_summary.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {summary.highlights_summary.slice(0, 3).map((highlight) => (
                      <Badge key={highlight} variant="outline" className="text-xs">
                        {highlight}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Reviews Yet */}
      {(!summary || summary.total_reviews === 0) && (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Award className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-white mb-2">
              Nenhuma avaliação ainda
            </h3>
            <p className="text-slate-500 mb-4">
              Seja o primeiro a compartilhar sua experiência!
            </p>
          </CardContent>
        </Card>
      )}

      {/* Reviews List */}
      {reviews.length > 0 && (
        <div className="space-y-4">
          <h3 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-orange-500" />
            Todas as avaliações
          </h3>
          
          {reviews.map((review, index) => (
            <motion.div
              key={review.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className={review.is_own_review ? 'border-orange-200 bg-orange-50/50' : ''}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <Avatar className="w-12 h-12">
                      <AvatarFallback className="bg-orange-100 text-lg">
                        {review.user_avatar || '😊'}
                      </AvatarFallback>
                    </Avatar>
                    
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white">
                            {review.user_name}
                            {review.is_own_review && (
                              <Badge variant="outline" className="ml-2 text-xs">Você</Badge>
                            )}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <StarRating value={review.overall_rating} readonly size="sm" />
                            <span className="text-sm text-slate-500 dark:text-slate-400">
                              {format(parseISO(review.created_at), "dd/MM/yyyy", { locale: ptBR })}
                            </span>
                          </div>
                        </div>
                        
                        {review.would_travel_again && (
                          <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                            <ThumbsUp className="w-3 h-3 mr-1" />
                            Viajaria novamente
                          </Badge>
                        )}
                      </div>
                      
                      <p className="text-slate-700 dark:text-slate-300 mt-3">
                        {review.comment}
                      </p>
                      
                      {review.highlights && review.highlights.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-3">
                          {review.highlights.map((highlight) => (
                            <Badge key={highlight} variant="outline" className="text-xs">
                              <Sparkles className="w-3 h-3 mr-1 text-yellow-500" />
                              {highlight}
                            </Badge>
                          ))}
                        </div>
                      )}
                      
                      {review.category_ratings && (
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-4 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                          {Object.entries(review.category_ratings).map(([key, value]) => (
                            <div key={key} className="text-center">
                              <p className="text-xs text-slate-500 dark:text-slate-400">{categoryLabels[key]}</p>
                              <div className="flex items-center justify-center gap-1">
                                <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                                <span className="font-medium text-slate-900 dark:text-white">{value}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
